import { Button, PhoneField, TextField, Typography } from "@mindlercare/ui-web";
import { FormEvent, useState } from "react";
import { useInternalAuth } from "../AuthenticationProvider";
import { LinkButton } from "../components/LinkButton";
import { useTranslation } from "../hooks/useTranslation";
import { useStateNavigator } from "../hooks/useStateNavigator";
import { BackToLogin } from "./BackToLogin";

export const CreateAccountButton = () => {
  const { t } = useTranslation();
  const { market } = useInternalAuth();
  const { navigate } = useStateNavigator();
  return (
    <div>
      <LinkButton
        underline
        color="BLACK"
        onClick={() => {
          navigate({ view: "create-account", market });
        }}
      >
        {t("CREATE_ACCOUNT")}
      </LinkButton>
    </div>
  );
};

export const CreateAccount = () => {
  const { t } = useTranslation();
  const { navigate } = useStateNavigator();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const { signUp, confirmSignUp, resendMFA, market } = useInternalAuth();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [requireConfirmSignUp, setRequireConfirmSignUp] = useState(false);
  const [confirmCode, setConfirmCode] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!requireConfirmSignUp) {
      try {
        const { success, nextStep } = await signUp(
          email.toLowerCase(),
          password,
          phoneNumber,
        );

        if (!success && nextStep === "CONFIRM_SIGN_UP") {
          setRequireConfirmSignUp(true);
        } else {
          navigate({ view: "email", market });
        }
      } catch (error: unknown) {
        let message = t("ERROR_UNKNOWN");

        if (error instanceof Error) {
          message = error.message;
        }

        setErrorMessage(message);
      }
    } else {
      try {
        await confirmSignUp(email.toLowerCase(), confirmCode);
      } catch (error: unknown) {
        console.error(error);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {!requireConfirmSignUp ? (
        <>
          <BackToLogin />
          <Typography variant="h3">{t("CREATE_NEW_ACCOUNT")}</Typography>
          <div style={{ marginTop: "24px" }}>
            <TextField
              placeholder={t("EMAIL_PLACEHOLDER")}
              label={t("EMAIL")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              hideSensitiveData={true}
              error={errorMessage.length > 0}
            />
            <div style={{ marginTop: "12px" }} />
            <TextField
              placeholder={t("PASSWORD_PLACEHOLDER")}
              label={t("PASSWORD")}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hideSensitiveData={true}
              error={errorMessage.length > 0}
              errorMessage={errorMessage}
            />
            <div style={{ marginTop: "12px" }} />
            <PhoneField
              label={`${t("PHONE_NUMBER")}*`}
              placeholder={t("PHONE_NUMBER_PLACEHOLDER")}
              initialCountry={"se"}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.value)}
            />
          </div>
          <div style={{ marginTop: "24px" }}>
            <Button
              variant="primary"
              disabled={!email || !password}
              type="submit"
            >
              {t("CREATE_ACCOUNT")}
            </Button>
          </div>
        </>
      ) : (
        <>
          <Typography variant="h3">{t("CONFIRM_SIGN_UP")}</Typography>
          <Typography variant="body">{t("CODE_HAS_BEEN_SENT")}</Typography>
          <div style={{ margin: "24px 0px" }}>
            <TextField
              placeholder={t("EMAIL_PLACEHOLDER")}
              label={t("EMAIL")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              hideSensitiveData={true}
              disabled={true}
            />
            <TextField
              placeholder={t("CODE")}
              label={`${t("CONFIRMATION_CODE")}*`}
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              hideSensitiveData={true}
            />
          </div>
          <div style={{ display: "flex", margin: "24px 0px" }}>
            <Typography variant="body">
              {t("LOST_CODE")}
              <LinkButton type="button" underline onClick={() => resendMFA()}>
                {t("RESEND_CODE")}
              </LinkButton>
            </Typography>
          </div>
          <Button variant="primary" disabled={!confirmCode} type="submit">
            {t("CREATE_ACCOUNT")}
          </Button>
        </>
      )}
    </form>
  );
};
